import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { LOGO_URL } from "@/data/site";

const word = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.1 + i * 0.08, duration: 0.9, ease: [0.19, 1, 0.22, 1] },
  }),
};

const HEADLINE = ["More", "than", "a", "digital", "marketing", "agency."];
const HEADLINE_2 = ["We", "are", "the", "messenger."];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end pb-16 pt-32 overflow-hidden grain"
    >
      {/* Animated background blobs */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-brand-gold/20 blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full bg-brand-brown/15 blur-3xl animate-blob" style={{ animationDelay: "-4s" }} />
        <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full bg-brand-maroon/10 blur-3xl animate-blob" style={{ animationDelay: "-8s" }} />
      </div>

      <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-24">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-4 mb-10"
        >
          <span className="w-10 h-px bg-brand-brown" />
          <p className="eyebrow">Creative Digital Agency · Est. 2022</p>
        </motion.div>

        {/* Headline */}
        <h1 data-testid="hero-headline" className="font-display font-medium leading-[0.95] tracking-tighter text-[11vw] md:text-[7.5vw] lg:text-[6vw] max-w-[18ch] text-brand-charcoal">
          <span className="block">
            {HEADLINE.map((w, i) => (
              <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.22em]">
                <motion.span variants={word} initial="hidden" animate="show" custom={i} className="inline-block">
                  {w}
                </motion.span>
              </span>
            ))}
          </span>
          <span className="block italic text-brand-brown">
            {HEADLINE_2.map((w, i) => (
              <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.22em] pr-[0.08em] pb-[0.08em]">
                <motion.span variants={word} initial="hidden" animate="show" custom={i + HEADLINE.length} className="inline-block">
                  {w}
                </motion.span>
              </span>
            ))}
          </span>
        </h1>

        {/* Bottom row */}
        <div className="mt-16 grid md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="md:col-span-6 lg:col-span-5 text-lg md:text-xl leading-relaxed text-brand-charcoal/75 max-w-xl"
          >
            Connecting your brand with the people who matter most — through creative storytelling, performance marketing, content and digital strategy.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 0.8 }}
            className="md:col-span-6 lg:col-span-7 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a
              data-testid="hero-cta-consultation"
              href="#contact"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-brand-gradient text-white text-sm font-semibold hover:opacity-90 shadow-lg shadow-brand-charcoal/20 transition-colors"
            >
              Book free consultation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              data-testid="hero-cta-work"
              href="#clients"
              className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-brand-charcoal/25 text-brand-charcoal text-sm font-semibold hover:bg-brand-charcoal hover:text-white hover:border-brand-charcoal transition-colors"
            >
              <Play size={14} className="fill-current" />
              View our work
            </a>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="mt-20 flex items-center justify-between text-xs uppercase tracking-[0.28em] text-brand-charcoal/50"
        >
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="w-7 h-7 rounded-full bg-brand-cream p-0.5 object-contain" />
            <span>DigiDoot Studio · Bengaluru South</span>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span>Scroll</span>
            <span className="w-10 h-px bg-brand-brown/40" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
